import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send, Sparkles, X } from "lucide-react";
import { API } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const AiAssistant = ({ courseId, moduleTitle, onClose }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/chat/${courseId}/history`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [courseId, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: courseId, module_title: moduleTitle, message: text, language: i18n.language }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "Erreur de connexion. Réessayez." }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-card/80 border-l border-primary/15" data-testid="ai-assistant-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-primary/10">
        <div className="flex items-center gap-2">
          <Sparkles size={16} strokeWidth={1.5} className="text-primary" />
          <span className="text-sm font-medium">{t("chat.title")}</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="ai-close-btn">
            <X size={16} />
          </button>
        )}
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {!user ? (
          <p className="text-sm text-muted-foreground">{t("chat.login")}</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground leading-relaxed">{t("chat.intro")}</p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary/10 border border-primary/20 rounded-lg px-4 py-2.5 ml-6" : "text-foreground/85"}`}
              data-testid={`chat-message-${m.role}`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          ))
        )}
      </div>
      {user && (
        <div className="p-4 border-t border-primary/10 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={t("chat.placeholder")}
            className="flex-1 bg-input border border-primary/20 rounded-full px-4 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
            data-testid="chat-input" />
          <button onClick={send} disabled={streaming}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-[#16528A] transition-colors duration-200 disabled:opacity-50"
            data-testid="chat-send-btn">
            <Send size={14} />
          </button>
        </div>
      )}
    </div>
  );
};
